import type { RoomThread, AudienceReply } from "../../shared/types.ts";
import {
  compareMostReplies,
  compareMostUpvotes,
  compareNewestThreads,
  compareOldestThreads,
} from "../../shared/threadSorting.ts";
import { sortableThread } from "./threads.ts";

export type BoardSortKey = "latest" | "latestReply" | "upvotes" | "replies";
export interface BoardSort {
  key: BoardSortKey;
  dir: "asc" | "desc";
}
export interface BoardReplyRow {
  kind: "reply";
  reply: AudienceReply;
  parent: RoomThread;
}
export type PendingRow = RoomThread | BoardReplyRow;

export interface BoardStats {
  pending: number;
  pendingAi: number;
  pendingAudience: number;
  pendingReplies: number;
  onBoard: number;
  onBoardAi: number;
  onBoardAudience: number;
  replies: number;
  repliesPending: number;
  answered: number;
  hidden: number;
  joined: number;
}
export interface BoardStrata {
  pending: PendingRow[];
  onBoard: RoomThread[];
  answered: RoomThread[];
  hidden: RoomThread[];
  stats: BoardStats;
}

function isAi(t: RoomThread): boolean {
  return t.threadOrigin === "ai";
}
function queuedReplies(t: RoomThread): AudienceReply[] {
  return (t.replies ?? []).filter((r) => r.status === "queued");
}

function sortOnBoard(threads: RoomThread[], sort: BoardSort): RoomThread[] {
  return [...threads].sort((a, b) => {
    const left = sortableThread(a);
    const right = sortableThread(b);
    if (sort.key === "upvotes") return compareMostUpvotes(left, right);
    if (sort.key === "replies") return compareMostReplies(left, right);
    if (sort.key === "latestReply") {
      const byActivity = right.lastActivityAt - left.lastActivityAt
        || right.createdAt - left.createdAt
        || left.id.localeCompare(right.id);
      return sort.dir === "desc" ? byActivity : -byActivity;
    }
    return sort.dir === "asc"
      ? compareOldestThreads(left, right)
      : compareNewestThreads(left, right);
  });
}

export function deriveBoardStrata(threads: RoomThread[], sort: BoardSort): BoardStrata {
  const pendingThreads = threads.filter((t) => t.status === "queued");
  const onBoard = threads.filter((t) => t.status === "shown");
  const answered = threads.filter((t) => t.status === "answered").sort((a, b) => b.createdAt - a.createdAt);
  const hidden = threads.filter((t) => t.status === "hidden").sort((a, b) => b.createdAt - a.createdAt);

  const replyRows: BoardReplyRow[] = onBoard.flatMap((parent) =>
    queuedReplies(parent).map((reply) => ({ kind: "reply" as const, reply, parent })),
  );

  const pending: PendingRow[] = [...pendingThreads, ...replyRows].sort((a, b) => {
    const at = "kind" in a ? a.reply.createdAt : a.createdAt;
    const bt = "kind" in b ? b.reply.createdAt : b.createdAt;
    return at - bt; // oldest first, always
  });

  const stats: BoardStats = {
    pending: pending.length,
    pendingAi: pendingThreads.filter(isAi).length,
    pendingAudience: pendingThreads.filter((t) => !isAi(t)).length,
    pendingReplies: replyRows.length,
    onBoard: onBoard.length,
    onBoardAi: onBoard.filter(isAi).length,
    onBoardAudience: onBoard.filter((t) => !isAi(t)).length,
    replies: threads.reduce((n, t) => n + (t.replies?.length ?? t.replyCount), 0),
    repliesPending: onBoard.filter((t) => queuedReplies(t).length > 0).length,
    answered: answered.length,
    hidden: hidden.length,
    joined: 0, // filled by caller from status.connectedClients
  };

  return { pending, onBoard: sortOnBoard(onBoard, sort), answered, hidden, stats };
}
